import { Button, Form } from 'devextreme-react';
import {
  ButtonItem,
  ButtonOptions,
  CustomRule,
  EmptyItem,
  GroupItem,
  Label,
  RequiredRule,
  SimpleItem,
} from 'devextreme-react/form';
import DataSource from 'devextreme/data/data_source';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { apiPostFiles, postApi } from '../../apiUtils';
import { Loader } from '../../components/loader/Loader';
import { useTranslation } from '../../contexts/translation';
import { swrConfig } from '../../pages/Assets/AssetsEdit';
import { convertMinsToHrsMins } from '../../pages/Tasks/Columns';
import { ApplicationUser } from '../../types/applicationUser.types';
import { Asset } from '../../types/asset.types';
import { CapacityCategory } from '../../types/capacityCategory.types';
import { Customer } from '../../types/customer.types';
import {
  CustomerProject,
  CustomerProjectBillingStatus,
  CustomerProjectBillingStatusTranslation,
} from '../../types/customerProject.types';
import { Person } from '../../types/person.types';
import { TaskStatus } from '../../types/TaskStatus';
import { getColorClass, getUserColorClass, UserColor } from '../../types/userColor.types';
import { WorkActivity } from '../../types/workActivity.types';
import { API_URL, fetcher } from '../../utils/apiUrl';
import {
  dateFieldOptionsWithSerialization,
  dateTimeFieldOptions,
  dateTimeFieldOptionsWithSerialization,
} from '../../utils/dates.types';
import { dxSelectBoxConfig } from '../tasks/TasksForm';
import './OutlookPluginTaskCreate.styles.scss';

export interface CreateFormData {
  customerId: number | null;

  requestorName: string;
  requestorEmail: string;
  taskName: string;
  description: string;
  attachmentsCount: number;
  emailReceived: string;

  inboxEmail: string;
  inboxFolderName: string;

  customerProjectId?: number;
  typeId?: number;
  assetId?: number;
  capacityCategoryId?: number;
  responsiblePersonId?: number;
  requestorPhone?: string;
  creationTime?: string;
}

interface Props {
  user: ApplicationUser;
  defaultFormData: CreateFormData;
  attachmentsFromPlugin: File[];
  disableCustomerId: boolean;
  assetClassId: number;
}

export const OutlookPluginTaskCreateForm = ({
  user,
  defaultFormData,
  attachmentsFromPlugin,
  disableCustomerId,
  assetClassId,
}: Props) => {
  const { translate } = useTranslation();
  const transFormName = 'TaskOutlookPlugin.Create';
  const formRef = useRef<any>();
  const formData = useRef<any>({});

  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    defaultFormData.customerId,
  );
  const [selectedTopLevelAssetId, setSelectedTopLevelAssetId] = useState<number | null>(null);
  const [selectedDocumentTypeId, setSelectedDocumentTypeId] = useState<number | null>(null);
  const [userColorClass, setUserColorClass] = useState<string | null>(null);
  const [isDefaultFromDataSet, setDefaultFormDataSet] = useState(false);
  const [formMounted, setFormMounted] = useState(false);
  const [files, setFiles] = useState<File[]>(attachmentsFromPlugin);
  const [activityStartedSet, setActivityStartedSet] = useState(false);
  const [isShowDowntime, setShowDowntime] = useState<boolean>(false);
  const [projectColorClass, setProjectColorClass] = useState<string | null>();

  const { data: customers, error: errorCustomers } = useSWR<Customer[]>(
    `${API_URL.CUSTOMER_LIST}`,
    fetcher,
    swrConfig,
  );
  const { data: projects, error: errorProjects } = useSWR<CustomerProject[]>(
    selectedCustomerId
      ? `${API_URL.CUSTOMER_PROJECT_LIST}?parentId=${selectedCustomerId}`
      : defaultFormData.customerId
      ? `${API_URL.CUSTOMER_PROJECT_LIST}?parentId=${defaultFormData.customerId}`
      : null,
    fetcher,
    swrConfig,
  );

  const { data: documentTypes, error: errorDocumentTypes } = useSWR<any[]>(
    `${API_URL.GET_LIST_FOR_TASK_CREATE}?parentId=${assetClassId}&taskCreateDesktop=true&visibleOnly=true&validateForUser=true`,
    fetcher,
    swrConfig,
  );
  const { data: topLevelAssets, error: errorTopLevelAssets } = useSWR<Asset[]>(
    `${API_URL.ASSET_LIST_BY_CLASS_TOP_LEVEL}?classId=${assetClassId}&activeOnly=true&visibleOnly=true`,
    fetcher,
    swrConfig,
  );
  const { data: assets, error: errorAssets } = useSWR<Asset[]>(
    selectedTopLevelAssetId
      ? `${API_URL.ASSET_LIST_BY_CLASS}?classId=${assetClassId}&parentId=${selectedTopLevelAssetId}&assetToInclude=${selectedTopLevelAssetId}`
      : `${API_URL.ASSET_LIST_BY_CLASS}?classId=${assetClassId}`,
    fetcher,
    swrConfig,
  );
  const { data: assetsAssignToCustomer } = useSWR<Asset[]>(
    selectedCustomerId
      ? `${API_URL.ASSET_LIST_FOR_CUSTOMER}?customerId=${selectedCustomerId}`
      : null,
    fetcher,
    swrConfig,
  );
  const { data: capacityCategories, error: errorCategories } = useSWR<CapacityCategory[]>(
    `${API_URL.CAPACITY_CATEGORY_LIST}?parentId=${assetClassId}`,
    fetcher,
    swrConfig,
  );
  const { data: responsiblePersons, error: errorResponsiblePersons } = useSWR<Person[]>(
    API_URL.PERSON_LIST_WITH_CAPACITY_CAT,
    fetcher,
  );

  const { data: userColors, error: errorColors } = useSWR<UserColor[]>(
    `${API_URL.USER_COLOR_LIST}`,
    fetcher,
    swrConfig,
  );

  const { data: statuses } = useSWR<TaskStatus[]>(
    selectedDocumentTypeId
      ? `${API_URL.TASK_PROCESS_CONFIG_LIST_BY_DOC_TYPE}?documentTypeId=${selectedDocumentTypeId}`
      : null,
    fetcher,
    swrConfig,
  );
  const { data: workActivities } = useSWR<WorkActivity[]>(
    `${API_URL.WORK_ACTIVITY_LIST_BY_ASSET_CLASS}?assetClassId=${assetClassId}&visibleOnly=true`,
    fetcher,
  );

  const {
    data: disableWorksheetTaskTimeValidation,
    isValidating: disableWorksheetTaskTimeValidationValidating,
  } = useSWR<boolean>(
    `${API_URL.GET_CONFIG_IS_ENABLED}?name=disableWorksheetTaskTimeValidation`,
    fetcher,
    {
      revalidateOnFocus: false,
    },
  );

  useEffect(() => {
    if (
      !customers ||
      !documentTypes ||
      !topLevelAssets ||
      !assets ||
      !capacityCategories ||
      !responsiblePersons ||
      isDefaultFromDataSet
    )
      return;

    if (!formMounted || !formRef.current) return;

    formRef.current.instance.updateData(defaultFormData);
    setDefaultFormDataSet(true);
  }, [
    formMounted,
    customers,
    documentTypes,
    topLevelAssets,
    assets,
    capacityCategories,
    responsiblePersons,
    isDefaultFromDataSet,
  ]);

  // Načetly se projekty (po výběru customera), automaicky vybereme, pokud je pouze jeden
  useEffect(() => {
    if (!isDefaultFromDataSet) return;
    if (!projects || projects.length !== 1) return;
    if (!formRef || !formRef.current) return;
    formRef.current.instance.updateData('customerProjectId', projects[0].id);
  }, [formRef.current, projects, isDefaultFromDataSet]);

  // Načetly se types, automaicky vybereme, pokud je pouze jeden
  useEffect(() => {
    if (!isDefaultFromDataSet) return;
    if (!documentTypes || documentTypes.length !== 1) return;
    if (!formRef || !formRef.current) return;
    formRef.current.instance.updateData('typeId', documentTypes[0].id);
  }, [formRef.current, documentTypes, isDefaultFromDataSet]);

  // Načetly se categories, automaicky vybereme, pokud je pouze jeden
  useEffect(() => {
    if (!isDefaultFromDataSet) return;
    if (!capacityCategories || capacityCategories.length !== 1) return;
    if (!formRef || !formRef.current) return;
    formRef.current.instance.updateData('capacityCategoryId', capacityCategories[0].id);
  }, [formRef.current, capacityCategories, isDefaultFromDataSet]);

  // Načetly se assety, pokud je pouze jeden, vybereme
  useEffect(() => {
    if (!isDefaultFromDataSet) return;
    if (!assets || assets.length !== 1) return;
    if (!formRef || !formRef.current) return;
    formRef.current.instance.updateData('assetId', assets[0].id);
  }, [formRef.current, assets, isDefaultFromDataSet]);

  // Načetly se responsible person, vyplníme přihlášeného uživatele (jen pokud šablona nenastavila)
  useEffect(() => {
    if (!isDefaultFromDataSet) return;
    if (!responsiblePersons) return;
    if (!formRef || !formRef.current) return;
    if (defaultFormData.responsiblePersonId) return;
    formRef.current.instance.updateData('responsiblePersonId', user.personalNumber);
  }, [formRef.current, responsiblePersons, isDefaultFromDataSet]);

  // Načetly se aktivity, vybereme první
  useEffect(() => {
    if (!isDefaultFromDataSet) return;
    if (!workActivities) return;
    if (!formRef || !formRef.current) return;
    formRef.current.instance.updateData('workActivityId', workActivities[0].id);
  }, [formRef.current, workActivities, isDefaultFromDataSet]);

  useEffect(() => {
    if (!defaultFormData.typeId) return;
    setSelectedDocumentTypeId(defaultFormData.typeId);
    const documentType = documentTypes?.find((x) => x.id === defaultFormData.typeId);
    if (documentType) setShowDowntime(documentType.showDowntime);
  }, [defaultFormData.typeId, documentTypes]);

  useEffect(() => {
    if (!formRef || !formRef.current) return;
    if (defaultFormData.customerProjectId)
      handleCustomerProjectChanged(defaultFormData.customerProjectId);
  }, [defaultFormData.customerProjectId, formRef]);

  useEffect(() => {
    if (!topLevelAssets || !defaultFormData.assetId || !assets) return;
    if (!formRef || !formRef.current) return;
    const asset = assets.find((x) => x.id === defaultFormData.assetId);
    if (asset) formRef.current.instance.updateData('topLevelAssetId', asset.topLevelParenId);
  }, [formRef.current, defaultFormData.assetId, topLevelAssets, assets]);

  // Při změně assetu se vybere parent
  const handleAssetChanged = useCallback(
    (assetId: number) => {
      if (selectedTopLevelAssetId || !assets) return;
      if (!formRef || !formRef.current) return;
      const asset: Asset = assets.filter((x) => x.id === assetId)[0];
      if (!asset) return;
      formRef.current.instance.updateData('topLevelAssetId', asset.topLevelParenId);
    },
    [formRef.current, selectedTopLevelAssetId, assets],
  );

  useEffect(() => {
    if (!projects || !defaultFormData.customerProjectId) return;
    const color = projects.find((x) => x.id === defaultFormData.customerProjectId);
    if (color) {
      // @ts-ignore
      setProjectColorClass(getColorClass(color.color));
    }

    if (formRef && formRef.current) handleCustomerProjectChanged(defaultFormData.customerProjectId);
  }, [defaultFormData.customerProjectId, projects, formRef, formRef.current]);

  // Při zěmně projektu se předvyplní billing status
  const handleCustomerProjectChanged = (customerProjectId: number) => {
    if (!projects || !customerProjectId || !formRef.current) return;
    const project = projects.filter((x) => x.id === customerProjectId)[0];
    if (!project) return;
    formRef.current.instance.updateData('billingStatus', project.defaultBillingStatus);
    if (project.assetId) {
      formRef.current.instance.updateData('topLevelAssetId', project.assetTopLevelParenId);
      formRef.current.instance.updateData('assetId', project.assetId);
    }
  };

  const handleResponsiblePersonChanged = (userColors: UserColor[], userId: number) => {
    const colorClass = getUserColorClass(userColors, userId);
    setUserColorClass(colorClass);
  };

  const onSubmit = async (e: any, targetTaskStatusIdForCreate?: number) => {
    if (e !== null) e.preventDefault();
    if (!formData) return;
    let resultId: number | null = null;

    resultId = await submitPost(targetTaskStatusIdForCreate);

    // @ts-ignore
    if (resultId && window.chrome && window.chrome.webview) {
      // @ts-ignore
      window.chrome.webview.postMessage(`${resultId}`);
    }

    return resultId;
  };

  const submitPost = useCallback(
    async (targetTaskStatusIdForCreate?: number) => {
      let resultId: number | null = null;

      await postApi({
        url: API_URL.TASK_POST,
        data: targetTaskStatusIdForCreate
          ? {
              ...formData.current,
              targetTaskStatusIdForCreate: targetTaskStatusIdForCreate,
              created: formData.current.emailReceived,
            }
          : formData.current,
        callAfterSuccess: async (data) => {
          resultId = data.id;
          if (files.length > 0) {
            await Promise.all(
              files.map(async (file: File) => {
                if (file.size <= 30000000) await fileUpload(file, data.id);
              }),
            );
          }

          // Worksheet
          if (formData.current.duration) {
            // založím task, vezmu jeho čas,. odečtu trvání = začátek výkazu
            // Rozparsování časové části (HH:mm)
            const durationParts = formData.current.duration.split(':');
            const durationHours = parseInt(durationParts[0], 10);
            const durationMinutes = parseInt(durationParts[1], 10);

            const worksheetFormData = {
              taskId: data.id,
              workActivityId: formData.current.workActivityId,
              workSheetStatusId: 20,
              operatorId: formData.current.responsiblePersonId,
              activityStarted: moment(formData.current.activityStarted).format('YYYY-MM-DDTHH:mm'),
              activityFinished: moment(formData.current.activityStarted)
                .add(durationHours, 'hours')
                .add(durationMinutes, 'minutes')
                .format('YYYY-MM-DDTHH:mm'),
              taskComplete: false,
            };

            await postApi({
              url: `${API_URL.WORKSHEET_POST2}?autoFinishOlder=false`,
              data: worksheetFormData,
            });
          }
        },
      });

      return resultId;
    },
    [formData, files],
  );

  const submitFormWithTaskState = (targetTaskStatusIdForCreate: number) => {
    if (formRef.current.instance.validate().isValid) onSubmit(null, targetTaskStatusIdForCreate);
  };

  const fileUpload = useCallback(async (file: File, taskId: number) => {
    try {
      const formData = new FormData();
      // can upload only 1 file
      formData.append('files', file);
      await apiPostFiles({
        url: `${API_URL.FILE_UPLOAD}?entity=Task&entityId=${taskId}`,
        data: formData,
      });
    } catch (err) {}
  }, []);

  const handleDeleteFile = (index: number) => {
    const _files = files.filter((f, i) => i !== index);
    setFiles(_files);
  };

  const handleFileValueChanged = (e) => {
    const selectedFiles: any = Array.from(e.target.files);
    setFiles([...files, ...selectedFiles]);
  };

  const projectsWithGroupName = projects?.map((project) => ({
    ...project,
    groupName: project.projectStatusIsActive
      ? translate!('Active projects', transFormName)
      : translate!('Inactive projects', transFormName),
  }));

  const saveTemplate = useCallback(async () => {
    await postApi({
      url: API_URL.TASK_TEMPLATE_CREATE_UPDATE,
      data: {
        id: 0,
        userId: user.personalNumber,
        assetClassId: assetClassId,
        inboxEmail: defaultFormData.inboxEmail,
        inboxFolderName: defaultFormData.inboxFolderName,
        ...formData.current,
      },
      callAfterSuccess: async (data) => {},
    });
  }, [formData, user, formRef, assetClassId, defaultFormData]);

  const onHtmlEditorContentReady = useCallback((e: any) => {
    const container = e.element.closest('.dx-htmleditor');
    if (container) {
      container.style.resize = 'vertical';
      container.style.overflow = 'auto';
      container.style.minHeight = '250px';
    }
  }, []);

  const htmlEditorOptions = useMemo(
    () => ({
      height: 250,
      resizable: { handles: 's' },
      toolbar: {
        items: [
          'undo',
          'redo',
          'bold',
          'italic',
          'underline',
          'strike',
          'alignLeft',
          'alignCenter',
          'alignRight',
          'alignJustify',
          'orderedList',
          'bulletList',
          'fontSize',
          'fontColor',
          'color',
          'background',
          'link',
          'clear',
        ],
      },
      mediaResizing: { enabled: true },
      onContentReady: onHtmlEditorContentReady,
    }),
    [],
  );

  console.log('--- ********************************************************************** ---');

  if (!userColors) console.log('Use colors not set');
  else console.log('Use colors set.');

  if (!disableWorksheetTaskTimeValidationValidating)
    console.log('disableWorksheetTaskTimeValidationValidating is loading');
  else console.log('disableWorksheetTaskTimeValidationValidating is loaded.');

  if (!projects) console.log('Projects not set');
  else console.log('Projects set');

  console.log('Selected customer id: ', selectedCustomerId);
  console.log('Default form data customer id: ', defaultFormData.customerId);

  if (
    !userColors ||
    disableWorksheetTaskTimeValidation === undefined ||
    (!projects && (selectedCustomerId || defaultFormData.customerId)) ||
    !customers ||
    !documentTypes ||
    !topLevelAssets ||
    !assets ||
    !capacityCategories ||
    !responsiblePersons
  )
    return <Loader />;

  console.log('!!!!!!!Rendering form!!!!!!!!!');

  if (!isDefaultFromDataSet) console.log('Form data not set');
  else console.log('Form data set');

  return (
    <>
      {!isDefaultFromDataSet && <Loader />}
      {isDefaultFromDataSet && <>Přihlášený uživatel: {user.fullName}</>}

      <form onSubmit={onSubmit} style={{ paddingBottom: 40 }}>
        <Form formData={formData.current} ref={formRef} labelLocation="left" colCount={3} onInitialized={() => setFormMounted(true)}>
          {isDefaultFromDataSet && (
            <GroupItem colCount={3} colSpan={3}>
              <SimpleItem
                dataField={'customerId'}
                editorType={'dxSelectBox'}
                editorOptions={{
                  ...dxSelectBoxConfig,
                  valueExpr: 'id',
                  displayExpr: 'name',
                  dataSource: customers,
                  onValueChanged: (e: any) => setSelectedCustomerId(e.value),
                  // readOnly: disableCustomerId,
                }}
              >
                <Label text={translate!('Customer', transFormName)} />
                <RequiredRule message={translate!('Customer is required', transFormName)} />
              </SimpleItem>
              <SimpleItem
                cssClass={`dropdown-user-color ${projectColorClass ? projectColorClass : ''}`}
                dataField={'customerProjectId'}
                editorType={'dxSelectBox'}
                editorOptions={{
                  ...dxSelectBoxConfig,
                  valueExpr: 'id',
                  displayExpr: 'name',
                  dataSource: new DataSource({
                    store: projectsWithGroupName,
                    group: {
                      selector: 'groupName',
                      desc: false,
                    },
                  }),
                  grouped: true,
                  groupExpr: 'groupName',
                  onValueChanged: (e: any) => {
                    handleCustomerProjectChanged(e.value);

                    if (!projects) return;
                    const color = projects.find((x) => x.id === e.value);
                    if (color) {
                      // @ts-ignore
                      setProjectColorClass(getColorClass(color.color));
                    }
                  },
                  itemTemplate: (data: any, _index: number, element: any) => {
                    if (!projects) return;
                    const color = projects.find((x) => x.id === data.id);
                    if (color)
                      element.innerHTML = `<div style="color: #${color.color}">${data.name}</div>`;
                    else element.innerHTML = `<div>${data.name}</div>`;
                  },
                }}
              >
                <Label text={translate!('Project', transFormName)} />
                <RequiredRule message={translate!('Project is required', transFormName)} />
              </SimpleItem>
              <SimpleItem
                dataField={'typeId'}
                editorType={'dxSelectBox'}
                editorOptions={{
                  ...dxSelectBoxConfig,
                  valueExpr: 'id',
                  displayExpr: 'name',
                  dataSource: documentTypes,
                  onValueChanged: (e: any) => {
                    const documentType = documentTypes?.find((x) => x.id === e.value);
                    if (documentType) setShowDowntime(documentType.showDowntime);
                    else setShowDowntime(false);
                    setSelectedDocumentTypeId(e.value);
                  },
                }}
              >
                <Label text={translate!('Type', transFormName)} />
                <RequiredRule message={translate!('Type is required', transFormName)} />
              </SimpleItem>

              <SimpleItem
                dataField={'topLevelAssetId'}
                editorType={'dxSelectBox'}
                editorOptions={{
                  ...dxSelectBoxConfig,
                  valueExpr: 'id',
                  displayExpr: 'name',
                  dataSource: selectedCustomerId
                    ? topLevelAssets?.filter((asset) =>
                        assetsAssignToCustomer?.some((assigned) => assigned.id === asset.id),
                      )
                    : topLevelAssets,
                  onValueChanged: (e: any) => setSelectedTopLevelAssetId(e.value),
                }}
              >
                <Label text={translate!('Parent asset', transFormName)} />
              </SimpleItem>
              <SimpleItem
                dataField={'assetId'}
                editorType={'dxSelectBox'}
                editorOptions={{
                  ...dxSelectBoxConfig,
                  valueExpr: 'id',
                  displayExpr: 'name',
                  dataSource: selectedCustomerId
                    ? assets?.filter((asset) =>
                        assetsAssignToCustomer?.some((assigned) => assigned.id === asset.id),
                      )
                    : assets,
                  onValueChanged: (e: any) => handleAssetChanged(e.value),
                }}
              >
                <Label text={translate!('Asset', transFormName)} />
                <RequiredRule message={translate!('Asset is required', transFormName)} />
              </SimpleItem>
              <SimpleItem
                dataField={'capacityCategoryId'}
                editorType={'dxSelectBox'}
                editorOptions={{
                  ...dxSelectBoxConfig,
                  valueExpr: 'id',
                  displayExpr: 'name',
                  dataSource: capacityCategories,
                }}
              >
                <Label text={translate!('Capacity category', transFormName)} />
                <RequiredRule
                  message={translate!('Capacity category is required', transFormName)}
                />
              </SimpleItem>
              <SimpleItem
                cssClass={`dropdown-user-color ${userColorClass}`}
                dataField="responsiblePersonId"
                editorType="dxSelectBox"
                editorOptions={{
                  ...dxSelectBoxConfig,
                  valueExpr: 'personalNumber',
                  displayExpr: 'fullName',
                  items: responsiblePersons,
                  onValueChanged: (e) => handleResponsiblePersonChanged(userColors, e.value),
                  itemTemplate: (data: any, _index: number, element: any) => {
                    const color = userColors.find((x) => x.userId === data.personalNumber);
                    if (color)
                      element.innerHTML = `<div style="color: #${color.color}">${data.fullName}</div>`;
                    else element.innerHTML = `<div>${data.fullName}</div>`;
                  },
                }}
              >
                <Label text={translate!('Responsible person', transFormName)} />
              </SimpleItem>

              <SimpleItem
                dataField="requiredDate"
                editorType="dxDateBox"
                editorOptions={{
                  ...dateFieldOptionsWithSerialization,
                  min: new Date(),
                }}
              >
                <Label text={translate!('Required date', transFormName)} />
              </SimpleItem>
              <SimpleItem
                dataField="creationTime"
                editorType="dxDateBox"
                editorOptions={{
                  ...dateTimeFieldOptions,
                }}
              >
                <Label text={translate!('Date of creation', transFormName)} />
                <RequiredRule message={translate!('Date of creation is required', transFormName)} />
              </SimpleItem>
              <GroupItem cssClass={'requestorGroup'}>
                <SimpleItem dataField="requestorName" editorType="dxTextBox">
                  <Label text={translate!('Requestor name', transFormName)} />
                </SimpleItem>
                <SimpleItem dataField="requestorEmail" editorType="dxTextBox">
                  <Label text={translate!('Requestor email', transFormName)} />
                  <CustomRule
                    message={translate!('Enter valid email', transFormName)}
                    validationCallback={(e) => {
                      return !e.value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.value);
                    }}
                  />
                </SimpleItem>
                <SimpleItem dataField="requestorPhone" editorType="dxTextBox">
                  <Label text={translate!('Requestor phone', transFormName)} />
                  <CustomRule
                    message={translate!('Enter valid phone number', transFormName)}
                    validationCallback={(e) => {
                      return (
                        !e.value ||
                        /^(\+?[0-9]{1,4}( ?))?[0-9]{3}( ?)[0-9]{3}( ?)[0-9]{3}$/.test(e.value)
                      );
                    }}
                  />
                </SimpleItem>
              </GroupItem>
              <GroupItem colCount={1}>
                <SimpleItem
                  dataField={'billingStatus'}
                  editorType={'dxSelectBox'}
                  editorOptions={{
                    ...dxSelectBoxConfig,
                    valueExpr: 'id',
                    displayExpr: 'name',
                    dataSource: Object.values(CustomerProjectBillingStatus).map(
                      (status: CustomerProjectBillingStatus) => {
                        return {
                          id: status,
                          name: translate!(
                            CustomerProjectBillingStatusTranslation[status],
                            `CustomersProjects.Status`,
                          ),
                        };
                      },
                    ),
                  }}
                >
                  <Label text={translate!('Billing status', transFormName)} />
                </SimpleItem>
                <GroupItem visible={isShowDowntime}>
                  <SimpleItem
                    dataField="downtimeFrom"
                    editorType="dxDateBox"
                    editorOptions={{
                      ...dateTimeFieldOptionsWithSerialization,
                    }}
                  >
                    <Label text={translate!('Downtime from', transFormName)} />
                    <RequiredRule message={translate!('Downtime from required', transFormName)} />
                  </SimpleItem>
                  <SimpleItem
                    dataField="downtimeTo"
                    editorType="dxDateBox"
                    editorOptions={{
                      ...dateTimeFieldOptionsWithSerialization,
                    }}
                  >
                    <Label text={translate!('Downtime to', transFormName)} />
                  </SimpleItem>
                </GroupItem>
              </GroupItem>
              <GroupItem cssClass={'worksheetGroup'}>
                <SimpleItem
                  dataField={'workActivityId'}
                  editorType={'dxSelectBox'}
                  editorOptions={{
                    ...dxSelectBoxConfig,
                    valueExpr: 'id',
                    displayExpr: 'name',
                    dataSource: workActivities,
                  }}
                >
                  <Label text={translate!('Work activity', transFormName)} />
                  <RequiredRule message={translate!('Work activity is required', transFormName)} />
                </SimpleItem>
                <SimpleItem
                  dataField="activityStarted"
                  editorType="dxDateBox"
                  editorOptions={{
                    ...dateTimeFieldOptions,
                    min: !disableWorksheetTaskTimeValidation
                      ? moment(defaultFormData.emailReceived)
                      : undefined,
                    max: moment(),
                    onValueChanged: (e) => {
                      e.component.option('value', e.value);
                      if (e.event) setActivityStartedSet(true);
                    },
                  }}
                >
                  <Label text={translate!('Activity started', transFormName)} />
                </SimpleItem>
                <SimpleItem
                  dataField="duration"
                  editorType="dxTextBox"
                  editorOptions={{
                    onValueChanged: (e) => {
                      if (e.value === undefined) return;

                      if (Number.isNaN(Number(e.value.replace(',', '.').replace(':', '.')))) {
                        return;
                      }

                      const newValue = e.value.trim();
                      let value = newValue.replace(',', '.').replace(':', '.');
                      if (value.includes('.')) {
                        const valueArr = value.split('.');
                        value = convertMinsToHrsMins(
                          Number(valueArr[0]) * 60 + Number(valueArr[1]),
                        );
                      } else {
                        value = convertMinsToHrsMins(value);
                      }

                      if (!activityStartedSet) {
                        const durationParts = value.split(':');
                        const durationHours = parseInt(durationParts[0], 10);
                        const durationMinutes = parseInt(durationParts[1], 10);

                        formRef.current.instance.updateData(
                          'activityStarted',
                          moment()
                            .subtract(durationHours, 'hours')
                            .subtract(durationMinutes, 'minutes')
                            .format('YYYY-MM-DDTHH:mm'),
                        );
                      }

                      e.component.option('value', value);
                    },
                  }}
                >
                  <Label text={translate!('Duration', transFormName)} />
                  <CustomRule
                    validationCallback={(e) => {
                      if (e.value === undefined) return true;
                      if (Number.isNaN(Number(e.value.replace(',', '.').replace(':', '.'))))
                        return false;
                      return true;
                    }}
                  />
                </SimpleItem>
              </GroupItem>
              <SimpleItem dataField="taskName" editorType="dxTextBox" colSpan={2}>
                <Label text={translate!('Name', transFormName)} />
                <RequiredRule message={translate!('Name is required', transFormName)} />
              </SimpleItem>
              <EmptyItem />
              <SimpleItem
                dataField="description"
                editorType="dxHtmlEditor"
                colSpan={3}
                editorOptions={{
                  ...htmlEditorOptions,
                  height: 280,
                }}
              >
                <Label location={'top'} text={translate!('Description', transFormName)} />
              </SimpleItem>
              <GroupItem colSpan={3}>
                <SimpleItem
                  dataField="diagnose"
                  editorType="dxHtmlEditor"
                  editorOptions={{
                    ...htmlEditorOptions,
                    height: 200,
                  }}
                >
                  <Label location={'top'} text={translate!('Diagnose', transFormName)} />
                </SimpleItem>
              </GroupItem>
              {!statuses || statuses.length <= 1 ? (
                <ButtonItem verticalAlignment={'center'} horizontalAlignment={'left'}>
                  <ButtonOptions
                    width={'100%'}
                    text={translate!('Save', transFormName)}
                    type={'success'}
                    useSubmitBehavior
                  />
                </ButtonItem>
              ) : (
                statuses.map((status, i) => {
                  return (
                    <ButtonItem
                      key={i}
                      colSpan={1}
                      verticalAlignment={'center'}
                      horizontalAlignment={'left'}
                    >
                      <ButtonOptions
                        width={'100%'}
                        text={`${translate!('Save', transFormName)} - ${
                          status.statusToNameTranslated
                        }`}
                        type={'success'}
                        useSubmitBehavior={false}
                        onClick={() => submitFormWithTaskState(status.statusToId)}
                      />
                    </ButtonItem>
                  );
                })
              )}
              <ButtonItem verticalAlignment={'center'} horizontalAlignment={'left'}>
                <ButtonOptions
                  width={'100%'}
                  text={translate!('Save as template', transFormName)}
                  type={'normal'}
                  useSubmitBehavior={false}
                  onClick={() => saveTemplate()}
                />
              </ButtonItem>
            </GroupItem>
          )}
        </Form>
        {isDefaultFromDataSet && (
          <>
            <div className="file-input">
              <input type="file" multiple onChange={handleFileValueChanged} />
            </div>
            <div className="files-container">
              <div className="title">{translate!('Files', transFormName)}:</div>
              <div className="files-list">
                {files.map((file, i) => {
                  return (
                    <div className="file" key={i}>
                      <div className="name">{file.name}</div>
                      <div className="buttons">
                        <Button
                          icon="remove"
                          className="remove"
                          stylingMode="text"
                          type="default"
                          onClick={() => handleDeleteFile(i)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </form>
    </>
  );
};
